import type { Journey } from '@/features/journeys'

type ResolveCompletionNextActionParams = {
  currentLevelUid: string | null
  apiNextLevelUid: string | null
  fallbackNextLevelUid: string | null
  isProgressRefreshPending: boolean
  didProgressRefreshFail: boolean
  isNextLevelLookupPending: boolean
  didNextLevelLookupFail: boolean
  isJourneyDataPending: boolean
  didJourneyDataFail: boolean
  hasJourneyData: boolean
}

export type CompletionNextAction =
  | { kind: 'loading' }
  | { kind: 'next-level'; targetLevelUid: string }
  | { kind: 'journey-finished' }
  | { kind: 'error' }

type FindFallbackNextLevelUidParams = {
  currentLevelUid: string | null
  journey: Journey | null
  finishedLevelIds: number[]
}

function sanitizeTargetLevelUid(
  targetLevelUid: string | null,
  currentLevelUid: string | null
) {
  if (!targetLevelUid || targetLevelUid === currentLevelUid) return null
  return targetLevelUid
}

export function findFallbackNextLevelUid({
  currentLevelUid,
  journey,
  finishedLevelIds
}: FindFallbackNextLevelUidParams) {
  if (!journey) return null

  const finishedSet = new Set(finishedLevelIds)
  const stages = [...(journey.active_stages ?? [])].sort(
    (a, b) => a.position - b.position || a.id - b.id
  )

  for (const stage of stages) {
    const levels = [...(stage.active_levels ?? [])].sort(
      (a, b) => a.position - b.position || a.id - b.id
    )

    for (const level of levels) {
      if (level.uid === currentLevelUid) continue
      if (finishedSet.has(level.id)) continue
      return level.uid
    }
  }

  return null
}

export function resolveCompletionNextAction({
  currentLevelUid,
  apiNextLevelUid,
  fallbackNextLevelUid,
  isProgressRefreshPending,
  didProgressRefreshFail,
  isNextLevelLookupPending,
  didNextLevelLookupFail,
  isJourneyDataPending,
  didJourneyDataFail,
  hasJourneyData
}: ResolveCompletionNextActionParams): CompletionNextAction {
  const directTarget = sanitizeTargetLevelUid(apiNextLevelUid, currentLevelUid)
  if (directTarget) {
    return { kind: 'next-level', targetLevelUid: directTarget }
  }

  if (isProgressRefreshPending || isJourneyDataPending) {
    return { kind: 'loading' }
  }

  if (didProgressRefreshFail || didJourneyDataFail) {
    return { kind: 'error' }
  }

  const fallbackTarget = sanitizeTargetLevelUid(
    fallbackNextLevelUid,
    currentLevelUid
  )
  if (fallbackTarget) {
    return { kind: 'next-level', targetLevelUid: fallbackTarget }
  }

  if (isNextLevelLookupPending) {
    return { kind: 'loading' }
  }

  if (didNextLevelLookupFail || !hasJourneyData) {
    return { kind: 'error' }
  }

  return { kind: 'journey-finished' }
}
